import JSZip from 'jszip';
import { AppState, Layer, LayerType, TemplateInfo } from '../types';

// ── Typy wewnętrzne (w pliku project.json) ───────────────────

interface SavedLayer {
  id:         string;
  fileName:   string;
  imageFile:  string;
  type:       LayerType;
  translateX: number;
  translateY: number;
  rotate:     number;
  baseScale:  number;
  scale:      number;
}

interface ProjectJson {
  version:      number;
  appState:     AppState;
  templateFile: string | null;
  templateMm:   { widthMm: number; heightMm: number } | null;
  layers:       SavedLayer[];
}

// ── Helpers ──────────────────────────────────────────────────

function makeThumbnail(dataUrl: string, size = 64): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = c.height = size;
      const ctx = c.getContext('2d')!;
      const s   = Math.min(size / img.width, size / img.height);
      const dw  = img.width * s, dh = img.height * s;
      ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
      resolve(c.toDataURL());
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

// ── Zapis projektu ───────────────────────────────────────────

export async function saveProject(
  appState: AppState,
  layers:   Layer[],
  template: TemplateInfo | null,
): Promise<void> {
  const zip = new JSZip();

  // Metadane warstw (bez danych obrazów)
  const savedLayers: SavedLayer[] = layers.map((l, i) => ({
    id:         l.id,
    fileName:   l.fileName,
    imageFile:  `layers/layer_${i}.png`,
    type:       l.type,
    translateX: l.translateX,
    translateY: l.translateY,
    rotate:     l.rotate,
    baseScale:  l.baseScale,
    scale:      l.scale,
  }));

  // Obrazy warstw
  const layersFolder = zip.folder('layers')!;
  for (let i = 0; i < layers.length; i++) {
    layersFolder.file(
      `layer_${i}.png`,
      layers[i].imageDataUrl.split(',')[1],
      { base64: true },
    );
  }

  // Obraz szablonu
  let templateFile: string | null = null;
  let templateMm:   { widthMm: number; heightMm: number } | null = null;
  if (template) {
    templateFile = 'template.png';
    templateMm   = { widthMm: template.widthMm, heightMm: template.heightMm };
    zip.file('template.png', template.dataUrl.split(',')[1], { base64: true });
  }

  const proj: ProjectJson = { version: 1, appState, templateFile, templateMm, layers: savedLayers };
  zip.file('project.json', JSON.stringify(proj, null, 2));

  const blob = await zip.generateAsync({
    type:               'blob',
    compression:        'DEFLATE',
    compressionOptions: { level: 6 },
  });

  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.download = 'project.pdd';
  a.href     = url;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Odczyt projektu ──────────────────────────────────────────

export interface LoadedProject {
  appState: AppState;
  layers:   Layer[];
  template: TemplateInfo | null;
}

export async function loadProject(
  file:         File,
  initialState: AppState,   // używany jako fallback dla nowych pól
): Promise<LoadedProject> {
  const buf = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(buf);

  const jsonStr = await zip.file('project.json')!.async('string');
  const proj: ProjectJson = JSON.parse(jsonStr);

  // Scalenie z initialState (backward compat – nowe pola mają wartości domyślne)
  const appState: AppState = { ...initialState, ...proj.appState };

  // Szablon
  let template: TemplateInfo | null = null;
  if (proj.templateFile && proj.templateMm) {
    const b64    = await zip.file(proj.templateFile)!.async('base64');
    const dataUrl = `data:image/png;base64,${b64}`;
    template = { dataUrl, ...proj.templateMm };
  }

  // Warstwy – ładowanie obrazów i generowanie miniatur
  const layers: Layer[] = await Promise.all(
    proj.layers.map(async sl => {
      const b64         = await zip.file(sl.imageFile)!.async('base64');
      const imageDataUrl = `data:image/png;base64,${b64}`;
      const thumbDataUrl = await makeThumbnail(imageDataUrl);
      return {
        id:               sl.id,
        fileName:         sl.fileName,
        imageDataUrl,
        thumbnailDataUrl: thumbDataUrl,
        type:             sl.type,
        translateX:       sl.translateX,
        translateY:       sl.translateY,
        rotate:           sl.rotate,
        baseScale:        sl.baseScale,
        scale:            sl.scale,
      };
    }),
  );

  return { appState, layers, template };
}
